import { AppStaticURL } from '@app/app.settings';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InputValidators } from '@app/core/validators/input.validator';
import { Router, ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DevicesClass, DeviceTypes } from '@app/core/classes';
import { BreadcrumbsDataModel } from '@app/core/models/breadcrumbs';
import { RoutingConfig } from '@app/routing/routing.config';
import { AccountService } from '@app/modules/account-modules/account.service';
import {
  AccountProfileVm,
  AccountUserInfo,
  getActiveBookingIndividualCustomerId,
  hasBookingIndividualCustomerAccount,
  hasMusicianAccountProfile
} from '@app/modules/account-modules/account.model';
import { AuthRoles, LocalRegistrationModel } from '@app/modules/auth-modules/auth.model';
import { MusicianService } from '@app/modules/musician-modules/musician.service';
import { ReplaySubject, Subscription, of } from 'rxjs';
import { CitiesService } from '@app/modules/cities-module/cities.service';
import { InstrumentsService } from '@app/modules/instruments-modules/instruments.service';
import { CityModel, InstrumentsModel, PositionModel } from '@app/modules/fundamentals-modules/fundamentals.models';
import { IMusicianUpdateMyInfo, IMusicianUserInfo } from '@app/modules/musician-modules/musician.model';
import { DialogIncludesTypes, DialogComponentModel } from '@app/core/models/diallog';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountEventBus, AccountStoryService } from '@app/modules/account-modules/account.story';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { FileToBase64, ObjectRequestFormatted } from '@app/utils/converter';
import { INSTLinkValidate, PhoneNumberValidate, URLLinkValidate, VKLinkValidate } from '@app/utils/validator';
import { PhoneRequestFormatted, VKRequestFormatted } from '@app/utils/converter';
import { mergeMap, finalize, map, take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { TechService } from '@app/core/services';
import { _deviceType } from '@app/utils/device';
import * as moment from 'moment';
declare var $;

/**
 * Components
 */
import { SearchCityComponent } from '@app/modules/cities-module/search-city/search-city.component';
import { SearchActivityComponent } from '@app/modules/activity-modules/search-activity/search-activity.component';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import {
  IndividualBookingCustomerDetailsVm,
  resolvedIndividualBookingCustomerCityId,
  UpdateIndividualCustomerProfileDto
} from '@app/modules/booking-modules/booking.model';
import { SocialPlatform, SocialLinkDto, socialPlatformFromApiValue } from '@app/modules/account-modules/performers.model';
import {
  SOCIAL_PLATFORMS,
  PLATFORM_ICON_MAP,
  PlatformIconConfig
} from '@app/modules/performers-modules/client/performer-form.constants';
import { ProfileIndividualCustomerCreateDialogComponent } from '@app/modules/account-modules/client/profile-individual-customer-create-dialog/profile-individual-customer-create-dialog.component';
import { ProfileIndividualCustomerCreateDialogData } from '@app/modules/account-modules/client/profile-individual-customer-create-dialog/profile-individual-customer-create-dialog.model';

/** Button labels: idle from static HTML (SSR); loading set via Renderer2 after click. */
const PHONE_CONFIRM_LABEL_IDLE = 'Отправить запрос на подтверждение';
const PHONE_CONFIRM_LABEL_LOADING = 'Отправка...';

export interface BaseErrorModel {
  status: boolean;
  value: string;
}
export interface ProfileErrorModels {
  name: BaseErrorModel;
  lastName: BaseErrorModel;
  email: BaseErrorModel;
  birthday: BaseErrorModel;
  cityId: BaseErrorModel;
  instrumentIds: BaseErrorModel;
  contactPhone: BaseErrorModel;
  password: BaseErrorModel;
  vkUsername: BaseErrorModel;
  instagramUsername: BaseErrorModel;
}

@UntilDestroy()
@Component({
  selector: 'app-account-profile-user',
  templateUrl: 'profile-user.component.html',
  styleUrls: [`./profile-user.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ProfileUserComponent extends DevicesClass implements OnInit {
  public StaticURL = AppStaticURL;

  @ViewChild('phoneConfirmBtn') phoneConfirmBtn: ElementRef<HTMLSpanElement>;

  /**
   * Base component variables.
   */
  public ProfileSubject: ReplaySubject<AccountUserInfo> = new ReplaySubject<AccountUserInfo>(1);
  public ProfileForm: FormGroup;
  public ProfileFormModify: boolean;
  public ProfileReconfirmModify: boolean;
  public ProfileLoading: boolean;
  public ProfileAvatar: string;
  public ProfileAvatarFile: FileList = null;
  public ProfileAvatarDelete: boolean;
  public ProfileCitySubject: ReplaySubject<CityModel> = new ReplaySubject<CityModel>(1);
  public ProfileCityValue: CityModel = null;
  public ProfileActivitySubject: ReplaySubject<any> = new ReplaySubject<any>(1);
  public ProfileActivityPosition: PositionModel = null;
  public ProfileActivityInstruments: InstrumentsModel = [];

  /**
   * Errors view variables
   */
  public ErrorsField: ProfileErrorModels;
  public ErrorsText: string;

  /**
   * Special component variables.
   */
  public fileAcceptString = 'image/x-png,image/gif,image/jpeg';

  public phoneVerified = false;
  public phoneConfirmLoading = false;

  /** Normalized digits for the number that was phoneVerified on the server — compared while editing. */
  private verifiedPhoneNormalizedSnapshot: string | null = null;

  /** Roles from the latest GET /account/userInfo (booking checkbox; legacy APIs). */
  public profileRoles: AuthRoles[] = [];
  /** `profiles` from the latest GET /account/userInfo (musician = Musician profileKind). */
  private accountProfiles: AccountProfileVm[] = [];
  /** Embedded individual booking-customer block (GET/PUT booking customer profile). */
  public bookingCustomerForm: FormGroup | null = null;
  public bookingProfileVm: IndividualBookingCustomerDetailsVm | null = null;
  public bookingCustomerProfileLoading = false;
  public bookingCustomerFormSubmitted = false;
  public bookingCustomerSaveError: string | null = null;
  public bookingCustomerProfileLoadError: string | null = null;
  public bookingCustomerAvatar: string | null = null;
  public bookingCustomerAvatarFile: FileList = null;
  public BookingCustomerCitySubject: ReplaySubject<CityModel> = new ReplaySubject<CityModel>(1);
  public BookingCustomerCityValue: CityModel = null;
  readonly SocialPlatforms = SOCIAL_PLATFORMS;
  private bookingCustomerId: string | null = null;
  private bookingFormDirtySub?: Subscription;
  /** Prevents duplicate create-booking-customer dialogs on repeated onAccountUpdate. */
  private bookingCustomerCreateDialogOpened = false;
  /** Instrument/position dialog for ?completeMusician=1 */
  private musicianCompleteDialogOpened = false;

  constructor(injector: Injector,
    public cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private inputValidators: InputValidators,
    private accountService: AccountService,
    private musicianService: MusicianService,
    private dialogService: DialogService,
    private citiesService: CitiesService,
    private instrumentsService: InstrumentsService,
    private accountStory: AccountStoryService,
    private notificationService: NotificationService,
    private techService: TechService,
    private renderer: Renderer2,
    private bookingService: BookingService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    /**
     * Build of profile form.
     */
    this.ProfileForm = this.formBuilder.group({
      id : ['', Validators.required],
      name : ['', Validators.required],
      lastName : [''],
      email : ['', Validators.compose([
        Validators.required,
        Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$')
      ])],
      positionId : [0],
      picPath : [''],
      cityId : [0, Validators.required],
      emailConfirmed : [true],
      instrumentIds : [[]],
      birthday : [''],
      contacts: this.formBuilder.group({
        vkUsername: ['', {validators: [this.inputValidators.checkVKValue]}],
        contactPhone: [null],
        hasTelegram: [false],
        hasMax: [false],
        instagramUsername: [''],
      })
    });

    /**
     * Set of default errors value.
     */
    this.ErrorsField = {
      name: {status: false, value: ''},
      lastName: {status: false, value: ''},
      email: {status: false, value: ''},
      birthday: {status: false, value: ''},
      cityId: {status: false, value: ''},
      instrumentIds: {status: false, value: ''},
      contactPhone: {status: false, value: 'Неправильный формат телефона'},
      password: {status: false, value: ''},
      vkUsername: {status: false, value: 'Неправильный формат ссылки'},
      instagramUsername: {status: false, value: 'Неправильный формат ссылки Instagram'}
    };

    /**
     * Get of profile data.
     */
    this.onAccountStoryBus();

    /**
     * Form changes control.
     */
    this.onProfileFormCtrl();
    this.onCityCtrl();
    this.onBookingCustomerCityCtrl();
    this.onActivityCtrl();
  }

  onAccountStoryBus(): void {
    this.accountStory.AccountEventBus$.pipe(untilDestroyed(this)).subscribe(
      emit => this.onAccountStoryListener(emit)
    );
  }

  onAccountStoryListener(emit: AccountEventBus): void {
    if (emit.event === 'onAccountUpdate') {
      const data = emit.data as AccountUserInfo;
      if (hasMusicianAccountProfile(data)) {
        this.onGetMusicianUserInfo();
      } else {
        this.resetMusicianActivityUi();
      }
      this.onSetProfileData(data);
    }

    if (emit.event === 'onAccountLogout' || emit.event === 'onAccountEmpty') {
      this.router.navigateByUrl(`/${RoutingConfig.Login}/${RoutingConfig.SignIn}?redirectUrl=${location.pathname}`).catch();
    }
  }

  onSetProfileData(response): void {
    this.phoneVerified = response.contacts?.phoneVerified === true;
    this.profileRoles = (response.roles || []) as AuthRoles[];
    this.accountProfiles = response.profiles || [];
    this.ProfileSubject.next(response);

    for (const key of Object.keys(response)) {
      switch (key) {
        case ('cityId') :
          this.onCityApply(response[key]);
        break;

        case ('picPath') :
          this.ProfileAvatar = response[key] ? AppStaticURL + response[key] : null;
          this.ProfileForm.controls[key].setValue(response[key], { emitEvent: false });
        break;

        case ('contacts') :
          for (const contact of Object.keys(response[key])) {
            if (this.onProfileContactsFormControl[contact]) {
              if (!response[key][contact]) {
                continue;
              }

              if (contact === 'contactPhone') {
                if (Number(response[key][contact]) === 0) {
                  continue;
                }
                this.onProfileContactsFormControl[contact].setValue(
                  '7' + response[key][contact], { emitEvent: false }
                );
              }

              if (contact === 'vkUsername') {
                this.onProfileContactsFormControl[contact].setValue(
                  'vk.com/' + response[key][contact], { emitEvent: false }
                );
                // https://
              }

              if (contact === 'hasTelegram') {
                this.onProfileContactsFormControl[contact].setValue(
                  response[key][contact], { emitEvent: false }
                );
              }

              if (contact === 'hasMax') {
                this.onProfileContactsFormControl[contact].setValue(
                  response[key][contact], { emitEvent: false }
                );
              }

              if (contact === 'instagramUsername' && this.onProfileContactsFormControl[contact]) {
                this.onProfileContactsFormControl[contact].setValue(
                  response[key][contact] || '', { emitEvent: false }
                );
              }
            }
          }
          break;
        default:
          if (this.ProfileForm.controls.hasOwnProperty(key)) {
            if (key === 'birthday') {
              this.onSetBirthdayValue(response[key], true);

            } else {
              this.ProfileForm.controls[key].setValue(response[key], { emitEvent: false });
            }
          }
      }
    }
    this.syncVerifiedPhoneSnapshotFromForm();
    this.refreshBookingCustomerSection(response as AccountUserInfo);
    this.tryOpenBookingCustomerCreateFromHireQuery(response as AccountUserInfo);
    this.tryOpenMusicianCompleteFromQuery(response as AccountUserInfo);
    this.cdr.markForCheck();
  }

  /**
   * Booking-customer profile creation from hire flow: ?createBookingCustomer=1&redirectUrl=/booking-customer/hire/...
   */
  private tryOpenBookingCustomerCreateFromHireQuery(user: AccountUserInfo): void {
    if (this.route.snapshot.queryParamMap.get('createBookingCustomer') !== '1') {
      return;
    }
    const rawRedirect = this.route.snapshot.queryParamMap.get('redirectUrl')?.trim() || null;
    const redirectUrl =
      rawRedirect && this.isSafeBookingHireRedirect(rawRedirect) ? rawRedirect : null;

    if (hasBookingIndividualCustomerAccount(user)) {
      if (redirectUrl) {
        this.router.navigateByUrl(redirectUrl).catch(() => {});
      } else {
        this.clearBookingCustomerHireQueryParams();
      }
      return;
    }

    if (this.bookingCustomerCreateDialogOpened) {
      return;
    }
    this.bookingCustomerCreateDialogOpened = true;

    const ref = this.dialogService.open(ProfileIndividualCustomerCreateDialogComponent, {
      header: 'Профиль заказчика',
      width: 'min(640px, 96vw)',
      data: {
        defaultDisplayName: user.name || '',
        defaultCityId: user.cityId && user.cityId > 0 ? user.cityId : 0,
        defaultSocialLinks: this.getDefaultBookingSocialLinksFromBaseProfile(user)
      } as ProfileIndividualCustomerCreateDialogData
    });

    ref.onClose.pipe(take(1)).subscribe((created: boolean | undefined) => {
      this.bookingCustomerCreateDialogOpened = false;
      if (created) {
        this.accountService.getMyInfo().subscribe(
          fresh => {
            this.accountService.onSetUserRole(fresh);
            this.onSetProfileData(fresh);
            if (redirectUrl) {
              this.router.navigateByUrl(redirectUrl).catch(() => {});
            } else {
              this.clearBookingCustomerHireQueryParams();
            }
            this.cdr.markForCheck();
          },
          () => {
            if (redirectUrl) {
              this.router.navigateByUrl(redirectUrl).catch(() => {});
            } else {
              this.clearBookingCustomerHireQueryParams();
            }
            this.cdr.markForCheck();
          }
        );
      } else {
        this.clearBookingCustomerHireQueryParams();
        this.cdr.markForCheck();
      }
    });
  }

  private clearBookingCustomerHireQueryParams(): void {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { createBookingCustomer: null, redirectUrl: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      })
      .catch(() => {});
  }

  private getDefaultBookingSocialLinksFromBaseProfile(user: AccountUserInfo): SocialLinkDto[] {
    const vkRaw = (user.contacts?.vkUsername || '').trim();
    if (!vkRaw) {
      return [];
    }
    let url = vkRaw;
    if (!/^https?:\/\//i.test(url)) {
      if (/^vk\.com\//i.test(url) || /^www\.vk\.com\//i.test(url)) {
        url = `https://${url}`;
      } else {
        url = `https://vk.com/${url.replace(/^\/+/, '')}`;
      }
    }
    return [{ platform: SocialPlatform.VK, url }];
  }

  private isSafeBookingHireRedirect(url: string): boolean {
    const u = (url || '').trim();
    return u.startsWith('/') && !u.startsWith('//') && !u.includes('://');
  }

  /**
   * Finish musician profile from performers search: ?completeMusician=1&redirectUrl=/performers
   */
  private tryOpenMusicianCompleteFromQuery(user: AccountUserInfo): void {
    if (this.route.snapshot.queryParamMap.get('completeMusician') !== '1') {
      return;
    }
    const rawRedirect = this.route.snapshot.queryParamMap.get('redirectUrl')?.trim() || null;
    const redirectUrl = rawRedirect && this.isSafeBookingHireRedirect(rawRedirect) ? rawRedirect : null;

    if (hasMusicianAccountProfile(user)) {
      if (redirectUrl) {
        this.router.navigateByUrl(redirectUrl).catch(() => {});
      } else {
        this.clearMusicianCompleteQueryParams();
      }
      return;
    }

    if (this.musicianCompleteDialogOpened) {
      return;
    }
    this.musicianCompleteDialogOpened = true;

    const subject = new ReplaySubject<any>(1);
    let handled = false;
    const sub = subject.pipe(take(1)).subscribe(value => {
      if (handled) {
        return;
      }
      const positionId = value?.position?.id;
      const instrumentIds = (value?.instruments || [])
        .map((i: { id: number }) => i.id)
        .filter((id: number) => id > 0);
      if (positionId === undefined || positionId === null || !instrumentIds.length) {
        handled = true;
        this.musicianCompleteDialogOpened = false;
        this.clearMusicianCompleteQueryParams();
        this.cdr.markForCheck();
        return;
      }
      handled = true;
      this.musicianService
        .addMusicianProfile({ positionId, instrumentIds })
        .pipe(untilDestroyed(this))
        .subscribe(
          () => {
            this.accountService.getMyInfo().subscribe(
              fresh => {
                this.accountService.onSetUserRole(fresh);
                this.onSetProfileData(fresh);
                this.musicianCompleteDialogOpened = false;
                if (redirectUrl) {
                  this.router.navigateByUrl(redirectUrl).catch(() => {});
                } else {
                  this.clearMusicianCompleteQueryParams();
                }
                this.cdr.markForCheck();
              },
              () => {
                this.musicianCompleteDialogOpened = false;
                this.clearMusicianCompleteQueryParams();
                this.cdr.markForCheck();
              }
            );
          },
          err => {
            const msg =
              (err && (err.message || err.error?.message)) ||
              (typeof err === 'string' ? err : '') ||
              'Не удалось создать профиль музыканта.';
            this.notificationService.onDialog({
              type: DialogIncludesTypes.PopupErrorTemplate,
              header: 'Ошибка',
              content: msg
            });
            this.musicianCompleteDialogOpened = false;
            this.clearMusicianCompleteQueryParams();
            this.cdr.markForCheck();
          }
        );
    });

    const ref = this.dialogService.open(SearchActivityComponent, {
      header: 'Выбрать инструмент и должность',
      width: _deviceType() === DeviceTypes.Mobile ? '90%' : '100%',
      styleClass: 'p-dialog__activity-search',
      data: <DialogComponentModel>{
        includeType: DialogIncludesTypes.ComponentTemplate,
        includeData: {
          dataInitial: {
            position: null,
            instruments: []
          },
          dataSubject: subject
        }
      }
    });

    ref.onClose.pipe(take(1)).subscribe(() => {
      sub.unsubscribe();
      if (!handled) {
        this.musicianCompleteDialogOpened = false;
        this.clearMusicianCompleteQueryParams();
        this.cdr.markForCheck();
      }
    });
  }

  private clearMusicianCompleteQueryParams(): void {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { completeMusician: null, redirectUrl: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      })
      .catch(() => {});
  }

  private clearBookingCustomerSection(): void {
    this.bookingFormDirtySub?.unsubscribe();
    this.bookingFormDirtySub = undefined;
    this.bookingCustomerId = null;
    this.bookingProfileVm = null;
    this.bookingCustomerForm = null;
    this.bookingCustomerProfileLoading = false;
    this.bookingCustomerFormSubmitted = false;
    this.bookingCustomerSaveError = null;
    this.bookingCustomerProfileLoadError = null;
    this.bookingCustomerAvatar = null;
    this.bookingCustomerAvatarFile = null;
    this.BookingCustomerCityValue = null;
  }

  private refreshBookingCustomerSection(user: AccountUserInfo): void {
    const id = getActiveBookingIndividualCustomerId(user);
    if (!id) {
      this.clearBookingCustomerSection();
      return;
    }
    this.bookingCustomerId = id;
    this.bookingCustomerProfileLoading = true;
    this.bookingCustomerSaveError = null;
    this.bookingCustomerProfileLoadError = null;
    this.cdr.markForCheck();
    this.bookingService
      .getIndividualBookingCustomerProfile(id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: data => {
          this.bookingCustomerProfileLoading = false;
          this.bookingProfileVm = data;
          this.buildBookingCustomerFormFromProfile(data);
          this.cdr.markForCheck();
        },
        error: () => {
          this.bookingCustomerProfileLoading = false;
          this.bookingProfileVm = null;
          this.bookingCustomerForm = null;
          this.bookingCustomerProfileLoadError = 'Не удалось загрузить профиль заказчика.';
          this.cdr.markForCheck();
        }
      });
  }

  private bookingCustomerCityMetaFromVm(
    data: IndividualBookingCustomerDetailsVm
  ): { id: number } | null {
    const id = resolvedIndividualBookingCustomerCityId(data);
    return id > 0 ? { id } : null;
  }

  private buildBookingCustomerFormFromProfile(data: IndividualBookingCustomerDetailsVm): void {
    this.bookingFormDirtySub?.unsubscribe();
    this.bookingCustomerAvatar = data.image?.path ? AppStaticURL + data.image.path : null;
    this.bookingCustomerAvatarFile = null;
    const cityMeta = this.bookingCustomerCityMetaFromVm(data);
    const cityIdNum = cityMeta?.id ?? 0;
    this.bookingCustomerForm = this.formBuilder.group({
      name: [(data.name || '').trim(), [Validators.maxLength(200)]],
      cityId: [cityIdNum > 0 ? cityIdNum : 0, [Validators.required, this.inputValidators.checkIdValue]],
      socialLinks: this.formBuilder.array([] as FormGroup[])
    });
    const arr = this.bookingCustomerForm.get('socialLinks') as FormArray;
    while (arr.length) {
      arr.removeAt(0);
    }
    for (const sl of data.socialLinks || []) {
      const plat = socialPlatformFromApiValue(sl.platform) ?? SocialPlatform.Instagram;
      arr.push(
        this.formBuilder.group({
          platform: [plat],
          url: [sl.url || '', (c: AbstractControl) => (c.value && !URLLinkValidate(c.value) ? { urlInvalid: true } : null)]
        })
      );
    }
    if (!arr.length) {
      this.addBookingCustomerSocialLink();
    }
    if (cityMeta && cityMeta.id > 0) {
      const dictCity = this.citiesService.onGetCityByDictionary(cityMeta.id);
      if (dictCity) {
        this.BookingCustomerCityValue = dictCity;
      } else {
        this.BookingCustomerCityValue = { id: cityMeta.id, name: `Город #${cityMeta.id}` };
      }
      this.bookingCustomerForm.get('cityId')?.setValue(cityMeta.id, { emitEvent: false });
    } else {
      this.BookingCustomerCityValue = null;
    }
    this.bookingCustomerForm.markAsPristine();
    this.wireBookingCustomerFormDirty();
  }

  private wireBookingCustomerFormDirty(): void {
    this.bookingFormDirtySub?.unsubscribe();
    if (!this.bookingCustomerForm) {
      return;
    }
    this.bookingFormDirtySub = this.bookingCustomerForm.valueChanges.subscribe(() => {
      this.ProfileFormModify = true;
      this.bookingCustomerSaveError = null;
      this.cdr.markForCheck();
    });
  }

  get bookingCustomerSocialLinksArray(): FormArray {
    return this.bookingCustomerForm?.get('socialLinks') as FormArray;
  }

  trackByBookingSocialIndex(index: number): number {
    return index;
  }

  getBookingPlatformIcon(platformValue: number): PlatformIconConfig {
    return PLATFORM_ICON_MAP[platformValue] ?? { type: 'svg', src: 'assets/images/icons/website.svg' };
  }

  onUploadBookingCustomerAvatarHandler(): void {
    const uploadInput = document.getElementById('bookingCustomerAvatarUpload');
    uploadInput?.click();
  }

  onUploadBookingCustomerAvatarEvent(files: FileList): void {
    for (const i of Object.keys(files)) {
      const file: File = files[i];
      if (!file) {
        continue;
      }
      if ((file.size / 1024) > 1024) {
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка загрузки',
          content: 'Превышен размер загружаемого файла. Ограничение на загрузку - 1Mb.',
        });
        return;
      }
      FileToBase64(file).then((value: string) => {
        this.bookingCustomerAvatarFile = files;
        this.bookingCustomerAvatar = value;
        this.ProfileFormModify = true;
        this.cdr.markForCheck();
      });
      return;
    }
  }

  onBookingCustomerAvatarError(): void {
    this.bookingCustomerAvatar = null;
    this.cdr.markForCheck();
  }

  addBookingCustomerSocialLink(): void {
    if (!this.bookingCustomerForm) {
      return;
    }
    this.bookingCustomerSocialLinksArray.push(
      this.formBuilder.group({
        platform: [SocialPlatform.Instagram],
        url: ['', (c: AbstractControl) => (c.value && !URLLinkValidate(c.value) ? { urlInvalid: true } : null)]
      })
    );
    this.bookingCustomerForm.markAsDirty();
    this.ProfileFormModify = true;
    this.cdr.markForCheck();
  }

  removeBookingCustomerSocialLink(i: number): void {
    this.bookingCustomerSocialLinksArray.removeAt(i);
    this.bookingCustomerForm?.markAsDirty();
    this.ProfileFormModify = true;
    this.cdr.markForCheck();
  }

  private getValidBookingSocialLinks(): SocialLinkDto[] {
    const rows = this.bookingCustomerSocialLinksArray?.controls as FormGroup[] | undefined;
    if (!rows?.length) {
      return [];
    }
    return rows
      .map(ctrl => ({
        platform: ctrl.get('platform').value as SocialPlatform,
        url: (ctrl.get('url').value || '').trim()
      }))
      .filter(l => !!l.url && URLLinkValidate(l.url));
  }

  private buildBookingCustomerUpdatePayload(): UpdateIndividualCustomerProfileDto | null {
    if (!this.bookingCustomerForm) {
      return null;
    }
    const v = this.bookingCustomerForm.getRawValue();
    return {
      name: ((v.name as string) || '').trim() || null,
      cityId: v.cityId as number,
      socialLinks: this.getValidBookingSocialLinks()
    };
  }

  /** Digits only, for comparing the current number to the verified snapshot. */
  private normalizePhoneDigits(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '';
    }
    return String(value).replace(/\D/g, '');
  }

  private syncVerifiedPhoneSnapshotFromForm(): void {
    if (this.phoneVerified) {
      const contactsCtrl: any = this.ProfileForm?.controls?.['contacts'];
      const raw = contactsCtrl?.controls?.contactPhone?.value;
      const n = this.normalizePhoneDigits(raw);
      this.verifiedPhoneNormalizedSnapshot = n.length > 0 ? n : null;
    } else {
      this.verifiedPhoneNormalizedSnapshot = null;
    }
  }

  private isMusicianProfileComplete(info: IMusicianUserInfo): boolean {
    const pos = Number(info?.positionId) || 0;
    const inst = info?.instrumentIds || [];
    return pos > 0 && inst.length > 0;
  }

  onGetMusicianUserInfo(): void {
    this.musicianService.musicianUserInfo().subscribe(
      response => {
        if (this.isMusicianProfileComplete(response)) {
          this.onActivityApply(response.positionId);
          this.onInstrumentsApply(response.instrumentIds);
        } else {
          this.resetMusicianActivityUi();
        }
        this.cdr.markForCheck();
      },
      () => this.resetMusicianActivityUi()
    );
  }

  resetMusicianActivityUi(): void {
    this.ProfileActivityPosition = null;
    this.ProfileActivityInstruments = [];
    this.ProfileForm.patchValue({
      positionId: 0,
      instrumentIds: []
    }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  get hasMusicianRole(): boolean {
    return hasMusicianAccountProfile({ profiles: this.accountProfiles });
  }

  get hasBookingCustomerRole(): boolean {
    return hasBookingIndividualCustomerAccount({ profiles: this.accountProfiles });
  }

  onProfileLoading(status: boolean, delay: number = 0) {
    setTimeout(() => {
      this.ProfileLoading = status;
      this.cdr.detectChanges();
    }, delay);
  }

  onReconfirmProfile(): void {
    if (this.ProfileReconfirmModify) {
      return;
    }

    this.accountService.resendEmail().subscribe(
      r => {
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupSuccessTemplate,
          header: 'Подтверждение профиля',
          content: 'Мы отправили Вам на почту, указанную в профиле, новое письмо для подтверждения учетной записи.'
        });
        this.ProfileReconfirmModify = true;
        this.cdr.markForCheck();
      },
      e => {
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupSuccessTemplate,
          header: 'Подтверждение профиля',
          content: 'Не удалось отправить повторно письмо подтверждения. Попробуйте повторить позднее.'
        });
      }
    )
  }

  onProfileFormCtrl(): void {
    for (const key of Object.keys(this.ErrorsField)) {
      if (this.ProfileForm.controls.hasOwnProperty(key)) {
        this.ProfileForm.controls[key].valueChanges.pipe(untilDestroyed(this)).subscribe(
          value => {
            this.ErrorsField[key].status = false;
            this.ProfileFormModify = true;
            this.ErrorsText = '';

            this.onToggleViewErrors(false);
          }
        );
      }
      if (this.onProfileContactsFormControl.hasOwnProperty(key)) {
        this.onProfileContactsFormControl[key].valueChanges.pipe(untilDestroyed(this)).subscribe(
          value => {
            this.ErrorsField[key].status = false;
            this.ProfileFormModify = true;
            this.ErrorsText = '';

            this.onToggleViewErrors(false);
            if (key === 'contactPhone') {
              this.cdr.markForCheck();
            }
          }
        )
      }
    }
  }

  onCityCtrl(): void {
    this.ProfileCitySubject.pipe(untilDestroyed(this)).subscribe(
      value => {
        this.ProfileCityValue = value;
        this.ProfileForm.controls.cityId.setValue(value.id);

        this.cdr.markForCheck();
      }
    );
  }

  onBookingCustomerCityCtrl(): void {
    this.BookingCustomerCitySubject.pipe(untilDestroyed(this)).subscribe(value => {
      this.BookingCustomerCityValue = value;
      if (this.bookingCustomerForm?.get('cityId')) {
        this.bookingCustomerForm.get('cityId').setValue(value.id);
      }
      this.cdr.markForCheck();
    });
  }

  onCityApply(id: number | string): void {
    const numId = Number(id);
    if (!numId || Number.isNaN(numId)) {
      return;
    }
    const CiyValue = this.citiesService.onGetCityByDictionary(numId);

    if (CiyValue) {
      this.ProfileCityValue = CiyValue;
      this.ProfileForm.controls.cityId.setValue(Number(CiyValue.id), { emitEvent: false });
    } else {
      /* Dictionary not loaded yet or city missing — keep id so the form and save stay valid. */
      this.ProfileForm.controls.cityId.setValue(numId, { emitEvent: false });
      this.ProfileCityValue = { id: numId, name: `Город #${numId}` };
    }
    this.cdr.markForCheck();
  }

  onInstrumentsApply(ids: Array<number>): void {
    this.ProfileActivityInstruments = [];
    ids.forEach(id => {
      const InstrumentValue = this.instrumentsService.onGetInstrumentByDictionary(id);
      this.ProfileActivityInstruments.push(InstrumentValue);
    });
    this.ProfileForm.controls.instrumentIds.setValue(ids, { emitEvent: false });
    this.cdr.markForCheck();
  }

  onBookingCustomerCityApply(id: number | string): void {
    const numId = Number(id);
    if (!numId || Number.isNaN(numId)) {
      return;
    }
    const cityValue = this.citiesService.onGetCityByDictionary(numId);
    if (cityValue) {
      this.BookingCustomerCityValue = cityValue;
      this.bookingCustomerForm?.get('cityId')?.setValue(Number(cityValue.id), { emitEvent: false });
    } else {
      this.bookingCustomerForm?.get('cityId')?.setValue(numId, { emitEvent: false });
      this.BookingCustomerCityValue = { id: numId, name: `Город #${numId}` };
    }
    this.cdr.markForCheck();
  }

  onActivityCtrl(): void {
    this.ProfileActivitySubject.pipe(untilDestroyed(this)).subscribe(
      value => {
        this.ProfileActivityPosition = value.position;
        this.ProfileForm.controls.positionId.setValue(value.position.id);

        this.ProfileActivityInstruments = value.instruments;
        this.ProfileForm.controls.instrumentIds.setValue(value.instruments.map(item => item.id));

        this.cdr.markForCheck();
      }
    );
  }

  onActivityApply(id: number): void {
    this.ProfileForm.controls.positionId.setValue(id, { emitEvent: false });
  }

  onUploadAvatarHandler(): void {
    const UploadInput = document.getElementById('avatarUpload');
    UploadInput.click();
  }

  onUploadAvatarEvent(files: FileList): void {
    for (const i of Object.keys(files)) {
      const File: File = files[i];

      if ((File.size / 1024) > 1024) {
        return this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка загрузки',
          content: 'Превышен размер загружаемого файла. Ограничение на загрузку - 1Mb.',
        });
      }

      FileToBase64(File).then((value: string) => {
        this.ProfileAvatarFile = files;
        this.ProfileFormModify = true;
        this.ProfileAvatar = value;
        this.cdr.markForCheck();
      });
    }
  }

  onUploadAvatarClear(): void {
    this.ProfileAvatarDelete = true;
    this.ProfileAvatar = null;
    this.ProfileFormModify = true;
    this.cdr.markForCheck();
  }

  onAvatarError() {
    this.ProfileAvatar = null;
    this.cdr.markForCheck();
  }

  onCheckPhone(): void {
    this.ErrorsField.contactPhone.status = !this.onProfileContactPhoneValid;
  }

  onRequestPhoneConfirm(): void {
    if (this.phoneConfirmLoading) {
      return;
    }
    if (!this.onProfilePhoneReadyForConfirm) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Телефон',
        content: 'Укажите корректный номер телефона.',
      });
      return;
    }
    const phone = String(this.onProfileContactsFormControl.contactPhone.value || '').trim();
    this.phoneConfirmLoading = true;
    this.setPhoneConfirmButtonText(PHONE_CONFIRM_LABEL_LOADING);
    this.cdr.markForCheck();
    this.techService
      .confirmPhoneNumber(phone)
      .pipe(
        finalize(() => {
          this.phoneConfirmLoading = false;
          this.setPhoneConfirmButtonText(PHONE_CONFIRM_LABEL_IDLE);
          this.cdr.markForCheck();
        })
      )
      .subscribe(
        () => {
          this.phoneVerified = true;
          this.syncVerifiedPhoneSnapshotFromForm();
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupSuccessTemplate,
            header: 'Телефон',
            content: 'Телефон подтверждён.',
            icons: 'pi pi-check'
          });
          this.accountService.onIniGetMyInfo();
        },
        (err: HttpErrorResponse) => {
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupErrorTemplate,
            header: 'Подтверждение телефона',
            content: this.extractHttpErrorMessage(err),
          });
        }
      );
  }

  private setPhoneConfirmButtonText(text: string): void {
    const el = this.phoneConfirmBtn && this.phoneConfirmBtn.nativeElement;
    if (el) {
      this.renderer.setProperty(el, 'textContent', text);
    }
  }

  private extractHttpErrorMessage(err: HttpErrorResponse): string {
    if (!err) {
      return 'Не удалось отправить запрос.';
    }
    const e = err.error as { message?: string } | string;
    if (typeof e === 'string' && e.length) {
      return e;
    }
    if (e && typeof e === 'object' && typeof e.message === 'string') {
      return e.message;
    }
    return err.message || 'Не удалось отправить запрос.';
  }

  onCheckFormattedDate(): void {
    const birthday = this.ProfileForm.controls.birthday.value || '';

    if (birthday === '') {
      return;
    }
    if (
      !moment(birthday, 'DD.MM.YYYY').isValid() ||
      moment(birthday, 'DD.MM.YYYY').year() > 2020 ||
      birthday.split('.').join('').length < 8
    ) {
      this.ErrorsField.birthday = {
        status: true,
        value: 'Неправильный формат даты'
      };
      this.cdr.detectChanges();
    }
  }

  onMaskPhone($event: any, backspace?: boolean): void {
    const contactsCtrl = <any>this.ProfileForm.controls['contacts'];
    const contactPhone: string = contactsCtrl.controls.contactPhone.value || null;

    if (!contactPhone || contactPhone === '') {
      return;
    }
    if (contactPhone.split('').length === 1) {
      if ($event.inputType === 'deleteContentBackward') {
        contactsCtrl.controls.contactPhone.setValue(contactPhone.split('').slice(1).join(''));
      } else {
        contactsCtrl.controls.contactPhone.setValue('7' + contactPhone);
      }
    }
  }

  onCheckSocial(type: string): void {
    if (type === 'vkUsername') {
      const ValueVK = this.onProfileContactsFormControl[type].value;

      if (ValueVK && ValueVK !== '') {
        this.ErrorsField[type].status = !VKLinkValidate(ValueVK);
      }
    }

    if (type === 'instagramUsername') {
      const value = this.onProfileContactsFormControl[type].value;
      if (value && value !== '') {
        this.ErrorsField.instagramUsername.status = !INSTLinkValidate(value);
      } else {
        this.ErrorsField.instagramUsername.status = false;
      }
      this.cdr.markForCheck();
    }
  }

  onGetCurrentDate(): Date {
    return new Date();
  }

  onSetBirthdayValue(value: string, formatted?: boolean): void {
    this.ProfileForm.controls.birthday.setValue(
      formatted ? moment(value).format('DD.MM.YYYY') : value, { emitEvent: false }
    );
  }

  onSetMessengerValue(key: string): void {
    if (!this.onProfileMessengerValid) {
      return;
    }
    const MessengerValue = this.onProfileContactsFormControl[key].value;

    this.onProfileContactsFormControl[key].patchValue(!MessengerValue);
  }

  onGetMessengerStatus(key: string): boolean {
    return this.onProfileContactsFormControl[key].value
  }

  onViewSearchCities(): void {
    this.dialogService.open(SearchCityComponent, {
      header: 'Выбрать город',
      width: _deviceType() === DeviceTypes.Mobile ? '90%' : '100%',
      styleClass: 'p-dialog__city-search',
      data: <DialogComponentModel>{
        includeType: DialogIncludesTypes.ComponentTemplate,
        includeData: {
          dataInitial: this.ProfileCityValue,
          dataSubject: this.ProfileCitySubject
        }
      }
    })
  }

  onViewBookingCustomerSearchCities(): void {
    if (!this.bookingCustomerForm) {
      return;
    }
    this.dialogService.open(SearchCityComponent, {
      header: 'Выбрать город',
      width: _deviceType() === DeviceTypes.Mobile ? '90%' : '100%',
      styleClass: 'p-dialog__city-search',
      data: <DialogComponentModel>{
        includeType: DialogIncludesTypes.ComponentTemplate,
        includeData: {
          dataInitial: this.BookingCustomerCityValue,
          dataSubject: this.BookingCustomerCitySubject
        }
      }
    });
  }

  onViewSearchActivity(): void {
    this.dialogService.open(SearchActivityComponent, {
      header: 'Выбрать инструмент и должность',
      width: _deviceType() === DeviceTypes.Mobile ? '90%' : '100%',
      styleClass: 'p-dialog__activity-search',
      data: <DialogComponentModel>{
        includeType: DialogIncludesTypes.ComponentTemplate,
        includeData: {
          dataInitial: {
            position: this.ProfileActivityPosition,
            instruments: this.ProfileActivityInstruments
          },
          dataSubject: this.ProfileActivitySubject
        }
      }
    })
  }

  onToggleViewErrors(show: boolean): void {
    const FieldErrors = $('.__profile__errors');
    show ? FieldErrors.slideDown(150) : FieldErrors.slideUp(150);
  }

  onGetFormattedDate(date: string): string {
    return date ? moment(date, 'DD.MM.YYYY').toISOString() : ''
  }

  onSaveProfile(): void {
    if (!this.ProfileFormModify || this.ProfileLoading) {
      return;
    }

    /**
     * Validation of Errors Fields.
     */
    for (const field of Object.keys(this.ErrorsField)) {
      if (this.ErrorsField[field].status) {
        this.ErrorsText = 'Проверьте правильность заполненных полей!';
        return this.onToggleViewErrors(true);
      }
    }

    /**
     * Initial validation.
     */
    if (!this.ProfileForm.valid) {
      for (const key of Object.keys(this.ProfileForm.controls)) {
        if (this.ErrorsField.hasOwnProperty(key)) {
          this.ErrorsField[key].status = !this.ProfileForm.controls[key].valid;

          if (key === 'cityId' && this.ProfileForm.controls[key].value === 0) {
            this.ErrorsField[key].status = true;
          }
        }
      }
      this.onProfileLoading(false);

      /**
       * View errors description.
       */
      this.ErrorsText = 'Обязательные поля не могут быть пустыми!';
      return this.onToggleViewErrors(true);
    }

    /**
     * Social Link validation.
     */
    if (this.ErrorsField.vkUsername.status) {
      this.onProfileLoading(false);
      this.ErrorsText = 'Проверьте правильность ссылок на социальные сети!';
      return this.onToggleViewErrors(true);
    }

    if (this.hasMusicianRole) {
      const raw = this.ProfileForm.getRawValue();
      const instIds = raw.instrumentIds || [];
      const posId = Number(raw.positionId);
      if (!instIds.length) {
        this.onProfileLoading(false);
        this.ErrorsField.instrumentIds.status = true;
        this.ErrorsField.instrumentIds.value = 'Укажите хотя бы один инструмент';
        this.ErrorsText = 'Для роли музыканта укажите инструмент.';
        return this.onToggleViewErrors(true);
      }
      if (!Number.isFinite(posId) || posId < 0) {
        this.onProfileLoading(false);
        this.ErrorsField.instrumentIds.status = true;
        this.ErrorsField.instrumentIds.value = 'Укажите должность или вариант без должности';
        this.ErrorsText = 'Для роли музыканта укажите должность (или «без должности»).';
        return this.onToggleViewErrors(true);
      }
    }

    if (this.hasBookingCustomerRole) {
      if (this.bookingCustomerProfileLoading || !this.bookingCustomerForm) {
        this.onProfileLoading(false);
        this.ErrorsText =
          'Профиль заказчика ещё загружается или недоступен. Подождите и обновите страницу.';
        return this.onToggleViewErrors(true);
      }
      this.bookingCustomerFormSubmitted = true;
      this.bookingCustomerSaveError = null;
      const socialOk = this.getValidBookingSocialLinks().length >= 1;
      if (!socialOk) {
        this.onProfileLoading(false);
        this.bookingCustomerSaveError = 'Добавьте хотя бы одну корректную ссылку на соцсеть.';
        this.ErrorsText = 'Проверьте раздел «Частный заказчик».';
        return this.onToggleViewErrors(true);
      }
      if (this.bookingCustomerForm.invalid) {
        this.onProfileLoading(false);
        this.bookingCustomerForm.markAllAsTouched();
        this.ErrorsText = 'Проверьте раздел «Частный заказчик».';
        return this.onToggleViewErrors(true);
      }
    }

    /**
     * Account raw value.
     */
    const AccountRawForm = <AccountUserInfo>this.ProfileForm.getRawValue();


    const AccountProfilePut = ObjectRequestFormatted(<LocalRegistrationModel>{
      name: AccountRawForm.name,
      lastName: AccountRawForm.lastName,
      email: AccountRawForm.email,
      phone: PhoneRequestFormatted(AccountRawForm.contacts?.contactPhone),
      birthday: this.onGetFormattedDate(AccountRawForm.birthday),
      cityId: AccountRawForm.cityId,
    });
    const MusicianUserInfo = <IMusicianUpdateMyInfo>{
      instrumentIds: this.ProfileForm.getRawValue().instrumentIds,
      positionId: this.ProfileForm.getRawValue().positionId
    };

    const contactsPayload = {
      vkUsername: VKRequestFormatted(AccountRawForm.contacts.vkUsername) || undefined,
      contactPhone: PhoneRequestFormatted(AccountRawForm.contacts.contactPhone) || undefined,
      hasTelegram: !!AccountRawForm.contacts.hasTelegram,
      hasMax: !!AccountRawForm.contacts.hasMax
    };

    /**
     * Loading.
     */
    this.onProfileLoading(true);



    /**
     * Update of account avatar.
     */
    if (this.ProfileAvatarFile) {
      this.accountService.uploadAvatar(this.ProfileAvatarFile).subscribe(
        r => {},
        e => {
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupErrorTemplate,
            header: 'Ошибка загрузки',
            content: 'Не удалось загрузить аватар профиля. Попробуйте использовать другой файл.',
          });
        }
      );
    }


    /**
     * Delete of account avatar
     */
    if (this.ProfileAvatarDelete) {
      this.accountService.deleteAvatar().subscribe(
        r => {},
        e => {
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupErrorTemplate,
            header: 'Ошибка удаления',
            content: 'Не удалось удалить аватар профиля',
          });
        }
      );
    }


    /**
     * Update of account profile.
     */
    const afterAccount = this.accountService.updateContacts(contactsPayload).pipe(
      mergeMap(() => this.accountService.updateMyInfo(AccountProfilePut))
    );
    const afterMusician = afterAccount.pipe(
      mergeMap(account =>
        this.hasMusicianRole
          ? this.musicianService.updateMyInfo(MusicianUserInfo).pipe(map(() => account))
          : of(account)
      )
    );
    const bookingPayload =
      this.hasBookingCustomerRole && this.bookingCustomerId && this.bookingCustomerForm
        ? this.buildBookingCustomerUpdatePayload()
        : null;
    const saveChain = afterMusician.pipe(
      mergeMap(account => {
        if (!bookingPayload || !this.bookingCustomerId) {
          return of(account);
        }
        return this.bookingService
          .updateIndividualBookingCustomerProfile(this.bookingCustomerId, bookingPayload)
          .pipe(map(() => account));
      }),
      mergeMap(account => {
        if (!this.bookingCustomerId || !this.bookingCustomerAvatarFile) {
          return of(account);
        }
        return this.bookingService
          .updateBookingCustomerMainPhoto(this.bookingCustomerId, this.bookingCustomerAvatarFile[0])
          .pipe(map(() => account));
      })
    );

    saveChain.subscribe(
      () => {
        this.onProfileLoading(false);
        this.bookingCustomerFormSubmitted = false;
        this.bookingCustomerSaveError = null;

        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupSuccessTemplate,
          header: 'Обновление профиля',
          content: 'Данные Вашего профиля успешно обновлены.',
          icons: 'pi pi-user-edit'
        });

        this.ProfileFormModify = false;
        this.accountService.onIniGetMyInfo();
      },
      () => {
        this.onProfileLoading(false);

        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка',
          content: 'Не удалось обновить Ваш профиль',
        });

        this.ProfileFormModify = false;
      }
    );
  }

  get onProfileContactPhoneValid(): boolean {
    const contactsCtrl: any = this.ProfileForm.controls['contacts'];
    const contactPhone: string = contactsCtrl.controls.contactPhone.value || '';

    return PhoneNumberValidate(contactPhone);
  }

  get onProfilePhoneReadyForConfirm(): boolean {
    try {
      const contactsCtrl: any = this.ProfileForm?.controls?.['contacts'];
      if (!contactsCtrl?.controls?.contactPhone) {
        return false;
      }
      const contactPhone: string = contactsCtrl.controls.contactPhone.value || '';
      return contactPhone.length > 0 && PhoneNumberValidate(contactPhone);
    } catch {
      return false;
    }
  }

  /** Show "phone verified": server flag is true and the current number matches the verified snapshot. */
  get isPhoneVerifiedForDisplay(): boolean {
    if (!this.phoneVerified) {
      return false;
    }
    const snap = this.verifiedPhoneNormalizedSnapshot;
    if (snap == null || snap === '') {
      return false;
    }
    try {
      const contactsCtrl: any = this.ProfileForm?.controls?.['contacts'];
      const raw = contactsCtrl?.controls?.contactPhone?.value;
      const current = this.normalizePhoneDigits(raw);
      return current.length > 0 && current === snap;
    } catch {
      return false;
    }
  }

  get onProfileMessengerValid(): boolean {
    const contactsCtrl: any = this.ProfileForm.controls['contacts'];
    const contactPhone: string = contactsCtrl.controls.contactPhone.value || '';

    return contactPhone !== '' && PhoneNumberValidate(contactPhone);
  }

  get onProfileFormControl(): any {
    return this.ProfileForm.controls;
  }

  get onProfileContactsFormControl(): any {
    return this.onProfileFormControl['contacts'].controls;
  }
}
